import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TextDialogComponent } from '@app/_dialogs/textdialog/textdialog.component';
import { replaceNewLineWithBr } from '@app/_helpers/functions/string-functions';
import { CommandResult } from '@app/_models/command/commandResult';
import { RequestTimeoutError } from '@app/_providers/httpErrorInterceptor';
import { RequestCancelledError } from '@app/_providers/requestCancelledError';
import { AccountService } from '@app/_services/account.service';
import { ApplicationService } from '@app/_services/application.service';
import * as Sentry from '@sentry/angular';
import { isAxiosError } from 'axios';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(BsModalService);
  private readonly accountService = inject(AccountService);
  private readonly applicationService = inject(ApplicationService);

  currentModalRef?: BsModalRef<TextDialogComponent>;

  /**
   * Handle error, which is expected to be an HttpErrorResponse, or an axios error from the orval client when Angular
   * code calls it.
   *
   * Useful for type safe usage in try/catch blocks
   */
  async handleError(error: unknown) {
    if (error instanceof RequestCancelledError) {
      return;
    }

    if (error instanceof HttpErrorResponse) {
      await this.displayErrorDialog(error);
    } else if (isAxiosError(error)) {
      await this.displayErrorDialog(
        new HttpErrorResponse({
          error: error.response?.data,
          status: error.response?.status ?? 0,
          url: error.config?.url,
          headers: new HttpHeaders(
            Object.fromEntries(
              Object.entries(error.response?.headers ?? {}).map(([name, value]) => [name, String(value)]),
            ),
          ),
        }),
      );
    } else {
      throw error;
    }
  }

  displayErrorDialog(errorResponse: HttpErrorResponse | RequestTimeoutError): Promise<boolean> | undefined {
    // Cancelled requests (status 0, e.g. during logout) are expected and need no dialog
    if (errorResponse instanceof RequestCancelledError) {
      return undefined;
    }

    if (errorResponse instanceof RequestTimeoutError) {
      Sentry.captureException(errorResponse);
      return new Promise<boolean>((resolve) => this.showBackendOfflineDialog(resolve));
    }

    return new Promise<boolean>((resolve) => {
      if (errorResponse.error && errorResponse.error instanceof Blob) {
        errorResponse.error.text().then((textError: string) => {
          if (errorResponse.error.type == 'application/json') {
            this.displayErrorDialogInternal(errorResponse, JSON.parse(textError), resolve);
          } else {
            this.displayErrorDialogInternal(errorResponse, textError, resolve);
          }
        });
      } else {
        this.displayErrorDialogInternal(errorResponse, errorResponse.error, resolve);
      }
    });
  }

  private displayErrorDialogInternal(
    errorResponse: HttpErrorResponse,
    error: unknown,
    resolve: (value: boolean | PromiseLike<boolean>) => void,
  ): void {
    if (
      errorResponse.status == 0 ||
      errorResponse.status == 408 ||
      errorResponse.status == 502 ||
      errorResponse.status == 503 ||
      errorResponse.status == 504
    ) {
      return this.showBackendOfflineDialog(resolve);
    } else if (errorResponse.status == 400) {
      if (Array.isArray(error)) {
        const errorText = this.errorsToHtmlList(error.map((x: { description: string }) => x.description));
        return this.showErrorDialog(errorText, resolve);
      } else if (error && typeof error === 'object' && 'description' in error) {
        const errorText = this.errorsToHtmlList([(error as { description: string }).description]);
        return this.showErrorDialog(errorText, resolve);
      } else if (typeof error === 'string') {
        const errorText = this.errorsToHtmlList([error]);
        return this.showErrorDialog(errorText, resolve);
      }
    } else if (errorResponse.status == 403) {
      // Set by JwtValidationMiddleware when the user as a whole is rejected (locked, deactivated or application
      // lockout), whichever request hits it first.
      if (errorResponse.headers.get('X-User-Locked-Out') === 'true') {
        this.applicationService.currentApplicationStatus$
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((appStatus) => {
            if (appStatus?.userLockoutEnabled) {
              // app is locked
              return this.showUserLockoutDialog(appStatus.lockoutText ?? '', resolve);
            }
            return this.showUserLockoutDialog(undefined, resolve);
          });
      } else {
        return this.showForbiddenDialog(resolve);
      }
    } else if (errorResponse.status == 429) {
      const retryAfter = Number(errorResponse.headers.get('Retry-After'));
      return this.showTooManyRequestsDialog(retryAfter, resolve);
    } else if (errorResponse.status == 404 || errorResponse.status == 405 || errorResponse.status == 500) {
      return this.showInternalServerErrorDialog(error, resolve);
    }

    return resolve(false);
  }

  errorsToHtmlList(errors: string[]): string {
    const uniqueErrors = [...new Set(errors.filter((error) => error != null))];

    const errorText = uniqueErrors
      .map((error) => {
        console.log(error);
        return `<li>${replaceNewLineWithBr(error)}</li>`;
      })
      .join('');

    return `<ol style="list-style: disc;">${errorText}</ol>`;
  }

  displayWarningDialog(
    commandResult: CommandResult,
    textBottom: string | undefined = undefined,
    okLabel?: string,
  ): Promise<BsModalRef<TextDialogComponent>> {
    return this.showWarningDialog(
      `<ul>${commandResult.warnings
        .map((warning) => `<li>${warning.description}</li>`)
        .join('')}</ul>${textBottom ? '<br>' + textBottom : ''}`,
      okLabel,
    );
  }

  handleDialogResult(
    modalRef: BsModalRef<TextDialogComponent>,
    resolve: (value: boolean | PromiseLike<boolean>) => void,
    callback?: (result: boolean) => void,
  ): void {
    this.currentModalRef = modalRef;

    if (modalRef.content) {
      modalRef.content.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_result) => {
        this.currentModalRef = undefined;
        resolve(true);
        callback?.(_result);
      });
    }
  }

  showErrorDialog(errorMessage: string, resolve: (value: boolean | PromiseLike<boolean>) => void): void {
    if (this.currentModalRef) {
      resolve(false);
      return;
    }

    TextDialogComponent.showDialogTranslated(
      this.translate,
      this.modalService,
      'ErrorHandler.BadRequestTitle',
      undefined,
      'Global.Ok',
      undefined,
      errorMessage,
      'modal-600',
    ).then((modalRef) => {
      this.handleDialogResult(modalRef, resolve);
    });
  }

  showWarningDialog(warningMessage: string, okLabel?: string): Promise<BsModalRef<TextDialogComponent>> {
    return TextDialogComponent.showDialogTranslated(
      this.translate,
      this.modalService,
      'ErrorHandler.WarningTitle',
      undefined,
      okLabel ?? 'Global.Ok',
      'Global.Cancel',
      warningMessage,
      'modal-600',
    );
  }

  showForbiddenDialog(resolve: (value: boolean | PromiseLike<boolean>) => void): void {
    if (this.currentModalRef) {
      resolve(false);
      return;
    }

    TextDialogComponent.showDialogTranslated(
      this.translate,
      this.modalService,
      'ErrorHandler.BadRequestTitle',
      'ErrorHandler.ForbiddenText',
      'Global.Ok',
      undefined,
    ).then((modalRef) => {
      this.handleDialogResult(modalRef, resolve);
    });
  }

  showUserLockoutDialog(message: string | undefined, resolve: (value: boolean | PromiseLike<boolean>) => void): void {
    if (this.currentModalRef) {
      resolve(false);
      return;
    }

    TextDialogComponent.showDialogTranslated(
      this.translate,
      this.modalService,
      'ErrorHandler.UserLockoutTitle',
      message ? undefined : 'ErrorHandler.UserLockoutText',
      'Nav.Links.Logout',
      undefined,
      message ?? '',
    ).then((modalRef) => {
      this.handleDialogResult(modalRef, resolve, () => {
        this.accountService.logout();
      });
    });
  }

  showBackendOfflineDialog(resolve: (value: boolean | PromiseLike<boolean>) => void): void {
    if (this.currentModalRef) {
      resolve(false);
      return;
    }

    TextDialogComponent.showDialogTranslated(
      this.translate,
      this.modalService,
      'ErrorHandler.BackendOfflineErrorTitle',
      'ErrorHandler.BackendOfflineErrorText',
      'ErrorHandler.ReloadApplication',
      'Global.Ok',
    ).then((modalRef) => {
      this.handleDialogResult(modalRef, resolve, (res) => {
        if (res) {
          globalThis.location.reload();
        }
      });
    });
  }

  showTooManyRequestsDialog(retryAfter: number, resolve: (value: boolean | PromiseLike<boolean>) => void): void {
    if (this.currentModalRef) {
      resolve(false);
      return;
    }

    TextDialogComponent.showDialogTranslated(
      this.translate,
      this.modalService,
      'ErrorHandler.TooManyRequestsErrorTitle',
      'ErrorHandler.TooManyRequestsErrorText',
      'Global.Ok',
      undefined,
      undefined,
      undefined,
      { retryAfter: retryAfter },
    ).then((modalRef) => {
      this.handleDialogResult(modalRef, resolve);
    });
  }

  showInternalServerErrorDialog(error: unknown, resolve: (value: boolean | PromiseLike<boolean>) => void): void {
    if (this.currentModalRef) {
      resolve(false);
      return;
    }

    if (error) {
      TextDialogComponent.showDialogTranslated(
        this.translate,
        this.modalService,
        'ErrorHandler.InternalServerErrorDevTitle',
        undefined,
        'Global.Ok',
        undefined,
        error instanceof Error ? error.message : JSON.stringify(error),
        'modal-lg',
      ).then((modalRef) => {
        this.handleDialogResult(modalRef, resolve);
      });
    } else {
      TextDialogComponent.showDialogTranslated(
        this.translate,
        this.modalService,
        'ErrorHandler.InternalServerErrorProdTitle',
        'ErrorHandler.InternalServerErrorProdText',
        'Global.Ok',
        undefined,
      ).then((modalRef) => {
        this.handleDialogResult(modalRef, resolve);
      });
    }
  }
}
